import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import type { UserCard } from '@/features/auth/types'
import { InviteList } from '@/features/friends/InviteList'
import { Face } from '@/features/game/Dice'
import { useForgetResumable, useResumable } from '@/features/game/useResumable'
import { QuestCard } from '@/features/quests/QuestCard'
import { busyWith, createGame, leaveGame, quickGame } from '@/shared/game/api'
import { rememberName, savedName } from '@/shared/game/storage'
import type { BotLevel, Variant } from '@/shared/game/types'
import { createLocalGame, createTutorial } from '@/shared/local/store'
import { DeviceIcon, DiceIcon, HelpIcon, LinkIcon, ResumeIcon, SearchIcon } from './icons'
import { MenuButton } from './MenuButton'
import { LEVEL_WORD, loadPrefs, type Prefs, SEATS_WORD, savePrefs } from './prefs'
import { startWithBots } from './start'

type Props = {
  me: UserCard | null
  onCreated: (gameId: string) => void
  onOpen: (gameId: string) => void
  /** Уйти на другой экран: вход, магазин. */
  go: (path: string) => void
}

/** Сложность компьютера — App\Domain\Ludo\Bot\BotLevel, от слабого к сильному. */
const LEVELS: BotLevel[] = ['easy', 'normal', 'hard']

/**
 * Главный экран: каждая кнопка — сразу партия.
 *
 * Раньше между «хочу играть» и доской стояли имя с клавиатуры и два-три
 * экрана вопросов: «с кем», «сколько игроков», «какая сложность». Мобильные
 * настолки так не делают: режим — одно нажатие, а всё, что можно не
 * спрашивать, не спрашивается. Имя гостю придумывается само
 * (storage.savedName), число игроков и сложность запоминаются
 * (prefs.ts) и меняются здесь же, одной строкой под кнопками.
 */
export function LobbyScreen({ me, onCreated, onOpen, go }: Props) {
  const [name, setName] = useState(savedName)
  const [prefs, setPrefs] = useState(loadPrefs)
  /*
   * Партия, в которую есть смысл вернуться, — и она же единственная
   * разрешённая: активная партия у человека одна. Проверяется всегда — и на
   * сервере, и в браузере (см. useResumable).
   */
  const resume = useResumable(me?.id ?? null)
  const forgetResume = useForgetResumable(me?.id ?? null)
  // Константой, а не resume.data по месту: внутри обработчиков TypeScript
  // сузить поле объекта не может, а константу — может.
  const active = resume.data ?? null
  /*
   * Вопрос про незаконченную партию — перехват нажатия, а не шаг: заходить
   * на него по ссылке незачем. Само нажатие не теряется: бросив прежнюю
   * партию, человек попадает туда, куда шёл.
   */
  const [busyAsk, setBusyAsk] = useState(false)
  const [pending, setPending] = useState<(() => void) | null>(null)

  const asName = () => (me === null ? name.trim() || savedName() : '')

  const choose = (next: Prefs) => {
    setPrefs(next)
    savePrefs(next)
  }

  /*
   * Прежде чем начать новую партию — спросить про прежнюю: сервер второй не
   * даст (409), а молча открыть вместо новой старую нельзя — нажали «с
   * компьютером», попали в чужую сетевую партию.
   */
  const guard = (run: () => void) => {
    if (active === null) {
      run()

      return
    }

    setPending(() => run)
    setBusyAsk(true)
  }

  /*
   * Гонка: партию начали в соседней вкладке, пока это меню висело открытым.
   * Тогда про прежнюю партию говорит не проверка, а отказ сервера.
   */
  const conflicted = (error: unknown) => {
    if (busyWith(error) === null) return

    void forgetResume()
    setBusyAsk(true)
  }

  const withBots = useMutation({
    mutationFn: () => startWithBots(asName(), prefs),
    onSuccess: onCreated,
    onError: conflicted,
  })

  /*
   * Быстрая игра — всегда за открытым столом с обычными правилами: делить
   * ищущих по правилам значило бы вдвое сократить тех, кого можно посадить.
   */
  const quick = useMutation({
    mutationFn: () => quickGame(asName()),
    onSuccess: (game) => onCreated(game.id),
    onError: conflicted,
  })

  /*
   * Стол для друга — закрытый: сядут только те, кому отправили ссылку.
   * Отдельного «открытого стола» больше нет — его и так открывает быстрая
   * игра, если ждущих нет.
   */
  const withFriend = useMutation({
    mutationFn: () => createGame(asName(), 'private', prefs.variant),
    onSuccess: (game) => onCreated(game.id),
    onError: conflicted,
  })

  /*
   * Бросить прежнюю партию и пойти туда, куда шли. Партия при этом кончается
   * насовсем, поэтому это отдельное нажатие, а не то, что делается молча.
   */
  const drop = useMutation({
    mutationFn: async () => {
      if (active !== null) await leaveGame(active.id)

      await forgetResume()
    },
    onSuccess: () => {
      setBusyAsk(false)
      pending?.()
      setPending(null)
    },
  })

  const busy = quick.isPending || withFriend.isPending || withBots.isPending || drop.isPending
  const failed = quick.isError || withFriend.isError || withBots.isError

  return (
    <div className="lobby">
      <div className="hero hero--row">
        {/* Кубики вместо картинки: игра узнаётся ими с первого взгляда. */}
        <div className="hero__dice" aria-hidden="true">
          <span className="die">
            <Face value={5} />
          </span>
          <span className="die">
            <Face value={2} />
          </span>
        </div>
        <h2 className="hero__title">Шешбеш</h2>
      </div>

      <InviteList enabled={me !== null} onOpen={onOpen} />

      {busyAsk && (
        <div className="card steps">
          <h2 className="card__title">У вас есть незаконченная партия</h2>

          {active === null ? (
            <p className="muted">проверяем…</p>
          ) : (
            <div className="menu">
              <MenuButton
                title="Продолжить"
                hint={`${active.label} · ${when(active.at)}`}
                icon={<ResumeIcon />}
                onClick={() => onOpen(active.id)}
              />
              {/* Второе действие — не «отмена»: доиграть партию можно, а
                  начать вторую нельзя, и выбор здесь именно между этим. */}
              <MenuButton
                title="Бросить и начать новую"
                hint="вернуться в неё будет уже нельзя"
                icon={<DiceIcon />}
                disabled={busy}
                onClick={() => drop.mutate()}
              />
            </div>
          )}

          <div className="row">
            <button
              className="ghost"
              type="button"
              onClick={() => {
                setPending(null)
                setBusyAsk(false)
              }}
            >
              ← назад
            </button>
            {drop.isError && (
              <span className="banner banner--error">не удалось бросить партию</span>
            )}
          </div>
        </div>
      )}

      <div className="menu">
        {/* Незаконченная партия — первой кнопкой: если она есть, то это и
            есть ответ на вопрос «во что играть». */}
        {active !== null && (
          <MenuButton
            title="Продолжить"
            hint={`${active.label} · ${when(active.at)}`}
            icon={<ResumeIcon />}
            onClick={() => onOpen(active.id)}
          />
        )}
        <MenuButton
          title="С компьютером"
          hint={`${SEATS_WORD[prefs.seats]} · ${LEVEL_WORD[prefs.level]}`}
          icon={<DiceIcon />}
          disabled={busy}
          onClick={() => guard(() => withBots.mutate())}
        />
        <MenuButton
          title="Быстрая игра"
          hint="с людьми по сети"
          icon={<SearchIcon />}
          disabled={busy}
          onClick={() => guard(() => quick.mutate())}
        />
        <MenuButton
          title="С другом"
          hint="позовите по ссылке"
          icon={<LinkIcon />}
          disabled={busy}
          onClick={() => guard(() => withFriend.mutate())}
        />
        {/* Сервер такой партии не нужен вовсе: играющие сидят рядом и
            подписаны цветом своих фишек. */}
        <MenuButton
          title="Один телефон"
          hint={`${SEATS_WORD[prefs.seats]}, по очереди`}
          icon={<DeviceIcon />}
          disabled={busy}
          onClick={() => guard(() => onCreated(createLocalGame(prefs.seats, null, prefs.variant)))}
        />
        {/* Последней: новичку она нужна один раз, остальным не нужна вовсе. */}
        <MenuButton
          title="Обучение"
          hint="правила за три хода"
          icon={<HelpIcon />}
          disabled={busy}
          onClick={() => guard(() => onCreated(createTutorial()))}
        />

        {failed && <p className="banner banner--error">не удалось создать партию</p>}
      </div>

      <PartyPrefs prefs={prefs} onChange={choose} />

      {/* Гостю — кто он за столом. Не поле перед кнопками, а строка под
          ними: сменить имя можно, но играть без этого можно тоже. */}
      {me === null && (
        <GuestName
          name={name}
          onChange={(next) => {
            setName(next)
            if (next.trim() !== '') rememberName(next.trim())
          }}
        />
      )}

      {/* Под меню, а не над ним: сначала выбор, во что играть, а задания —
          повод, который читают, когда выбор уже сделан. */}
      <QuestCard
        signedIn={me !== null}
        onSignIn={() => go('/login')}
        onOpenShop={() => go('/shop')}
      />
    </div>
  )
}

/**
 * Как играть: сколько за столом, сложность компьютера, правила.
 *
 * Свёрнуто в строку: меняют это редко, а видеть, что выбрано, надо всегда —
 * по этой строке понятно, что запустит кнопка. Развёрнутое — три ряда
 * переключателей, каждый сохраняется сразу, без кнопки «применить».
 */
function PartyPrefs({ prefs, onChange }: { prefs: Prefs; onChange: (prefs: Prefs) => void }) {
  const [open, setOpen] = useState(false)
  const rules = prefs.variant === 'bonus' ? 'сокровища' : 'классика'

  return (
    <div className="card prefs">
      <button
        className="prefs__summary"
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <span className="prefs__value">
          {SEATS_WORD[prefs.seats]} · {LEVEL_WORD[prefs.level]} · {rules}
        </span>
        <span className="prefs__toggle">{open ? 'готово' : 'изменить'}</span>
      </button>

      {open && (
        <div className="prefs__rows">
          <Pick
            label="Игроков"
            value={prefs.seats}
            options={[2, 3, 4].map((seats) => ({
              value: seats as Prefs['seats'],
              label: `${seats}`,
            }))}
            onChange={(seats) => onChange({ ...prefs, seats })}
          />
          <Pick
            label="Компьютер"
            value={prefs.level}
            options={LEVELS.map((level) => ({ value: level, label: LEVEL_WORD[level] }))}
            onChange={(level) => onChange({ ...prefs, level })}
          />
          <Pick
            label="Правила"
            value={prefs.variant}
            options={[
              { value: 'classic' as Variant, label: 'классика' },
              { value: 'bonus' as Variant, label: 'сокровища' },
            ]}
            onChange={(variant) => onChange({ ...prefs, variant })}
          />
        </div>
      )}
    </div>
  )
}

/** Ряд переключателя: подпись и сегменты, выбранный залит. */
function Pick<T extends string | number>({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: T
  options: { value: T; label: string }[]
  onChange: (value: T) => void
}) {
  return (
    <div className="prefs__row">
      <span className="prefs__name">{label}</span>
      <div className="segmented">
        {options.map((option) => (
          <button
            key={option.value}
            className={option.value === value ? 'segmented__on' : ''}
            type="button"
            aria-pressed={option.value === value}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  )
}

/**
 * Имя гостя — строкой «Вы играете как Ёж 42», по нажатию — поле.
 *
 * Пустым имя не бывает: стёртое поле возвращает прежнее, а не оставляет
 * гостя безымянным — сервер без имени за стол не посадит.
 */
function GuestName({ name, onChange }: { name: string; onChange: (name: string) => void }) {
  const [editing, setEditing] = useState(false)

  if (editing) {
    return (
      <form
        className="guest-name"
        onSubmit={(event) => {
          event.preventDefault()
          setEditing(false)
        }}
      >
        <label className="sr-only" htmlFor="player-name">
          Ваше имя
        </label>
        <input
          id="player-name"
          // biome-ignore lint/a11y/noAutofocus: поле открыли нажатием — печатать в него и собирались
          autoFocus
          value={name}
          onChange={(event) => onChange(event.target.value)}
          onBlur={() => {
            if (name.trim() === '') onChange(savedName())
            setEditing(false)
          }}
          maxLength={40}
        />
        <button className="ghost" type="submit">
          готово
        </button>
      </form>
    )
  }

  return (
    <p className="guest-name">
      <span className="muted">Вы играете как</span> <b>{name}</b>
      <button className="ghost" type="button" onClick={() => setEditing(true)}>
        сменить
      </button>
    </p>
  )
}

/**
 * Когда в партию ходили последний раз.
 *
 * Дата с временем целиком: партия, брошенная вчера, и партия, брошенная
 * пять минут назад, — это разные решения, и «недавно» тут не помогает.
 */
function when(at: number): string {
  return new Date(at).toLocaleString('ru')
}
